/**
 * Sub-header for a rules tab: version line, a Reload action and a read-only version list.
 * Replaces the branch/deploy/merge chrome, which does not apply to rules files.
 */
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { rulesApi } from "@/services/rules/api";
import { rulesDisplayText, type RulesScope } from "@/services/rules/types";
import { useRulesDocuments, useRulesVersions, rulesKeys } from "@/hooks/rules";
import { useRulesBaseVersionStore } from "@/stores/rulesBaseVersionStore";
import { useMetadataEditorStore } from "@/stores/metadataEditorStore";

interface RulesBarProps {
  tabId: string;
  scope: RulesScope;
  orgId: string;
  readOnly: boolean;
  isDirty: boolean;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export function RulesBar({ tabId, scope, orgId, readOnly, isDirty }: RulesBarProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const queryClient = useQueryClient();
  const base = useRulesBaseVersionStore((s) => s.versions[`${orgId}:${scope}`]);
  const setBase = useRulesBaseVersionStore((s) => s.setBase);
  const markSaved = useMetadataEditorStore((s) => s.markSaved);
  const { data: cachedDocs } = useRulesDocuments({ enabled: false });
  const maxBytes = cachedDocs?.max_bytes ?? null;
  const { data: versions, isLoading, isError } = useRulesVersions(scope, { enabled: historyOpen });

  const handleReload = async () => {
    if (isDirty && !window.confirm("Discard your unsaved edits and reload the latest version?")) return;
    try {
      const docs = await rulesApi.get(orgId);
      const doc = docs[scope];
      queryClient.setQueryData(rulesKeys.documents(orgId), docs);
      setBase(orgId, scope, doc.version);
      markSaved(tabId, rulesDisplayText(doc), undefined);
    } catch (error) {
      console.error("Failed to reload rules", error);
      toast.error("Could not reload the rules file.");
    }
  };

  return (
    <div className="border-b border-border bg-muted/30 text-xs">
      <div className="flex items-center gap-3 px-3 py-1.5">
        <span className="text-muted-foreground">
          {scope === "org" ? "Organization rules" : "Your rules"}
          {base !== undefined ? ` · version ${base}` : ""}
          {maxBytes !== null ? ` \u00b7 limit ${maxBytes} bytes` : ""}
          {readOnly ? " · read-only (admins can edit)" : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleReload}
            className="px-2 py-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="px-2 py-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            {historyOpen ? "Hide history" : "History"}
          </button>
        </div>
      </div>
      {historyOpen && (
        <ul className="max-h-40 overflow-auto px-3 pb-2 space-y-0.5">
          {isLoading && <li className="text-muted-foreground">Loading versions...</li>}
          {isError && <li className="text-destructive">Could not load versions.</li>}
          {versions?.length === 0 && <li className="text-muted-foreground">No saved versions yet.</li>}
          {versions?.map((v) => (
            <li key={v.version} className="flex gap-2 text-muted-foreground">
              <span className="font-mono text-foreground">v{v.version}</span>
              <span>{v.author_user_id ?? ""}</span>
              <span className="ml-auto">{formatWhen(v.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
